import React, { useMemo, useState } from "react";

/**
 * AttendanceLog — GitHub-contribution-style calendar.
 *
 * Props:
 *  - data: Array<{ date: "YYYY-MM-DD", status: "present" | "absent" | "leave" }>
 *          Missing dates are rendered as "no-data" (neutral gray).
 *  - weeks: number of weeks to render (default 52)
 *  - endDate: last day shown, defaults to today (Date or "YYYY-MM-DD")
 *  - theme: "light" | "dark" (default "light")
 *
 * NOTE: No attendance API contract exists yet (confirmed only for
 * /api/departments and /api/users). This component is data-agnostic —
 * feed it real records once an endpoint exists. Until then, use
 * generateDummyAttendance() below for wiring/testing.
 */

const STATUS_COLOR = {
  present: { light: "#39d353", dark: "#39d353" },
  absent: { light: "#f85149", dark: "#f85149" },
  leave: { light: "#d29922", dark: "#d29922" }, // optional third state, safe to ignore
  "no-data": { light: "#ebedf0", dark: "#2d333b" },
};

const DAY_MS = 24 * 60 * 60 * 1000;

function toDateKey(d) {
  return d.toISOString().slice(0, 10);
}

function buildWeeks(endDate, weeks) {
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  // Align to the Saturday of end's week so the grid's last column is complete.
  const endDow = end.getDay(); // 0=Sun..6=Sat
  const alignedEnd = new Date(end.getTime() + (6 - endDow) * DAY_MS);
  const totalDays = weeks * 7;
  const start = new Date(alignedEnd.getTime() - (totalDays - 1) * DAY_MS);

  const cols = [];
  for (let w = 0; w < weeks; w++) {
    const col = [];
    for (let d = 0; d < 7; d++) {
      const dayDate = new Date(start.getTime() + (w * 7 + d) * DAY_MS);
      col.push(dayDate);
    }
    cols.push(col);
  }
  return cols;
}

export function generateDummyAttendance({ days = 365, absentRate = 0.08 } = {}) {
  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < days; i++) {
    const d = new Date(today.getTime() - i * DAY_MS);
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue; // skip weekends, treated as no-data
    const roll = Math.random();
    const status = roll < absentRate ? "absent" : "present";
    out.push({ date: toDateKey(d), status });
  }
  return out;
}

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export default function AttendanceLog({
  data = [],
  weeks = 52,
  endDate = new Date(),
  theme = "light",
}) {
  const [hovered, setHovered] = useState(null); // { date, status, x, y }

  const recordsByDate = useMemo(() => {
    const map = new Map();
    for (const rec of data) map.set(rec.date, rec.status);
    return map;
  }, [data]);

  const cols = useMemo(() => buildWeeks(endDate, weeks), [endDate, weeks]);

  const monthMarkers = useMemo(() => {
    const markers = [];
    let lastMonth = null;
    cols.forEach((col, colIdx) => {
      const firstOfCol = col[0];
      const m = firstOfCol.getMonth();
      if (m !== lastMonth) {
        markers.push({ colIdx, label: MONTH_LABELS[m] });
        lastMonth = m;
      }
    });
    return markers;
  }, [cols]);

  const { presentCount, absentCount } = useMemo(() => {
    let p = 0, a = 0;
    for (const status of recordsByDate.values()) {
      if (status === "present") p++;
      else if (status === "absent") a++;
    }
    return { presentCount: p, absentCount: a };
  }, [recordsByDate]);

  const cell = 12;
  const gap = 3;
  const gridWidth = cols.length * (cell + gap);
  const gridHeight = 7 * (cell + gap);

  return (
    <div
      style={{
        fontFamily: "inherit",
        color: theme === "dark" ? "#c9d1d9" : "#24292f",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Attendance</div>
        <div style={{ fontSize: 12, opacity: 0.75, display: "flex", gap: 12 }}>
          <span>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: STATUS_COLOR.present[theme], marginRight: 4 }} />
            Present: {presentCount}
          </span>
          <span>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: STATUS_COLOR.absent[theme], marginRight: 4 }} />
            Absent: {absentCount}
          </span>
        </div>
      </div>

      <div style={{ overflowX: "auto", position: "relative" }}>
        <svg
          width={gridWidth + 20}
          height={gridHeight + 16}
          style={{ display: "block" }}
        >
          {monthMarkers.map((m) => (
            <text
              key={m.colIdx}
              x={m.colIdx * (cell + gap)}
              y={10}
              fontSize={10}
              fill={theme === "dark" ? "#8b949e" : "#57606a"}
            >
              {m.label}
            </text>
          ))}

          <g transform="translate(0, 16)">
            {cols.map((col, colIdx) =>
              col.map((date, rowIdx) => {
                const key = toDateKey(date);
                const status = recordsByDate.get(key) || "no-data";
                const isFuture = date.getTime() > new Date().setHours(0, 0, 0, 0);
                const fill = isFuture
                  ? theme === "dark" ? "#22272e" : "#f6f8fa"
                  : STATUS_COLOR[status][theme];

                return (
                  <rect
                    key={key}
                    x={colIdx * (cell + gap)}
                    y={rowIdx * (cell + gap)}
                    width={cell}
                    height={cell}
                    rx={2}
                    ry={2}
                    fill={fill}
                    stroke={theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(27,31,36,0.06)"}
                    onMouseEnter={(e) =>
                      setHovered({
                        date: key,
                        status,
                        x: e.clientX,
                        y: e.clientY,
                      })
                    }
                    onMouseMove={(e) =>
                      setHovered((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h))
                    }
                    onMouseLeave={() => setHovered(null)}
                    style={{ cursor: isFuture ? "default" : "pointer" }}
                  />
                );
              })
            )}
          </g>
        </svg>

        {hovered && (
          <div
            style={{
              position: "fixed",
              left: hovered.x + 12,
              top: hovered.y + 12,
              background: theme === "dark" ? "#1c2128" : "#24292f",
              color: "#fff",
              fontSize: 11,
              padding: "4px 8px",
              borderRadius: 4,
              pointerEvents: "none",
              zIndex: 10000,
              whiteSpace: "nowrap",
            }}
          >
            {hovered.status === "no-data" ? "No record" : hovered.status} — {hovered.date}
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6, fontSize: 11, opacity: 0.7 }}>
        <span>Less</span>
        {["no-data", "absent", "present"].map((s) => (
          <span
            key={s}
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: 2,
              background: STATUS_COLOR[s][theme],
            }}
          />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}